<template>
  <div class="user-orders">
    <p v-if="sortedOrders.length > 0" class="orders-notice">
      <i class="fas fa-envelope" aria-hidden="true"></i>
      Las actualizaciones de tus pedidos se envían a tu email. Revisá bandeja de entrada y spam.
    </p>

    <div v-if="props.isLoading || localLoading" class="orders-skeleton" aria-busy="true">
      <div v-for="i in 3" :key="i" class="skeleton-card"></div>
    </div>

    <div v-else-if="sortedOrders.length > 0" class="orders-stack">
      <article
        v-for="order in sortedOrders"
        :key="order.id"
        class="order-card"
      >
        <header class="order-card__head">
          <router-link :to="orderDetailPath(order.id)" class="order-card__identity">
            <span class="order-card__id">Pedido #{{ order.id }}</span>
            <time class="order-card__date">{{ formatDate(order.created_at) }}</time>
          </router-link>
          <span class="status-pill" :class="`status-pill--${order.status}`">
            <i :class="getStatusIcon(order.status)" aria-hidden="true"></i>
            {{ getStatusLabel(order.status) }}
          </span>
        </header>

        <ul v-if="getOrderLineItems(order).length" class="order-card__lines">
          <li
            v-for="product in previewLineItems(order)"
            :key="product.id || `${product.product}-${product.variant_display}`"
          >
            <img
              v-if="product.product_image"
              :src="product.product_image"
              :alt="product.product_name"
              class="line-thumb"
            />
            <span v-else class="line-thumb line-thumb--ph" aria-hidden="true">
              <i class="fas fa-seedling"></i>
            </span>
            <div class="line-body">
              <span class="line-name">{{ product.product_name }}</span>
              <span
                v-if="product.variant_display && !String(product.product_name).includes(product.variant_display)"
                class="line-variant"
              >{{ product.variant_display }}</span>
            </div>
            <span class="line-qty">×{{ product.quantity }}</span>
            <span class="line-price">{{ formatPrice(product.price_at_time) }}</span>
          </li>
          <li v-if="extraLineCount(order) > 0" class="order-card__more">
            <router-link :to="orderDetailPath(order.id)">
              Ver {{ extraLineCount(order) }} producto{{ extraLineCount(order) === 1 ? '' : 's' }} más
            </router-link>
          </li>
        </ul>
        <p v-else class="order-card__empty-lines">Sin detalle de productos</p>

        <ul class="order-card__meta">
          <li>
            <i class="fas fa-box" aria-hidden="true"></i>
            {{ orderLineCount(order) }}
            {{ orderLineCount(order) === 1 ? 'producto' : 'productos' }}
          </li>
          <li>
            <i class="fas fa-wallet" aria-hidden="true"></i>
            {{ paymentMethodLabel(order.payment_method) }}
          </li>
          <li v-if="order.shipping_service_name || order.delivery_type === 'pickup'">
            <i class="fas fa-truck" aria-hidden="true"></i>
            {{ order.delivery_type === 'pickup' ? 'Acordar envío · sin cargo en este pedido' : order.shipping_service_name }}
          </li>
          <li v-else-if="order.shipping_address">
            <i class="fas fa-map-marker-alt" aria-hidden="true"></i>
            <span class="order-card__meta-truncate">{{ order.shipping_address }}</span>
          </li>
        </ul>

        <footer class="order-card__foot">
          <div class="order-card__total">
            <span class="order-card__total-label">Total</span>
            <span class="order-card__total-value">{{ formatPrice(order.total) }}</span>
            <span
              v-if="Number(order.shipping_cost) > 0"
              class="order-card__total-hint"
            >
              incl. envío {{ formatPrice(order.shipping_cost) }}
            </span>
          </div>
          <div class="order-card__toolbar">
            <router-link
              :to="orderDetailPath(order.id)"
              class="btn btn-sm btn-primary order-card__btn"
            >
              Ver detalle completo
            </router-link>
            <OrderCustomerActions
              :order="order"
              :mercado-pago-enabled="mercadoPagoEnabled"
              layout="compact"
            />
            <button
              v-if="order.status === 'pending' || order.status === 'payment_pending'"
              type="button"
              class="btn btn-sm btn-link order-card__cancel"
              @click="confirmCancelOrder(order)"
            >
              Cancelar
            </button>
          </div>
        </footer>
      </article>
    </div>

    <div v-else class="orders-empty">
      <div class="orders-empty__icon" aria-hidden="true">
        <i class="fas fa-shopping-bag"></i>
      </div>
      <h2 class="orders-empty__title">No tenés pedidos</h2>
      <p class="orders-empty__text">Cuando hagas una compra, aparecerá acá con su estado y opciones de pago.</p>
      <router-link :to="shopRoute" class="btn btn-primary orders-empty__btn">
        <i class="fas fa-store me-2" aria-hidden="true"></i>
        Ir a la tienda
      </router-link>
    </div>

    <div class="modal fade" id="confirmationModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content order-modal-content">
          <div class="modal-header border-bottom border-secondary">
            <h5 class="modal-title">{{ modalTitle }}</h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">{{ modalMessage }}</div>
          <div class="modal-footer border-top border-secondary">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cerrar</button>
            <button
              type="button"
              class="btn btn-danger"
              @click="handleConfirmAction"
              :disabled="isProcessing"
            >
              <span v-if="isProcessing">
                <i class="fas fa-spinner fa-spin me-2"></i>Procesando…
              </span>
              <span v-else>Confirmar</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useToast } from 'vue-toastification';
import { cancelOrder, getUserOrders } from '@/services/orders';
import { getMercadoPagoConfig } from '@/services/payment';
import { getOrderLineItems, paymentMethodLabel as paymentMethodLabelUtil } from '@/utils/orderDisplay';
import { isIngresoHost } from '@/utils/host';
import OrderCustomerActions from '@/components/orders/OrderCustomerActions.vue';
import { defineProps } from 'vue';

const PREVIEW_LINES = 3;

const props = defineProps({
  isLoading: {
    type: Boolean,
    default: false
  },
  shopRoute: {
    type: String,
    default: '/productos'
  },
  paymentReturnPrefix: {
    type: String,
    default: ''
  }
});

const toast = useToast();
const bootstrap = window.bootstrap;
const userOrders = ref([]);
const localLoading = ref(true);

const modalTitle = ref('');
const modalMessage = ref('');
const selectedOrder = ref(null);
const currentAction = ref('');
const isProcessing = ref(false);
const mercadoPagoEnabled = ref(false);

const orderDetailPath = (id) =>
  isIngresoHost() ? `/ingreso/pedidos/${id}` : `/panel/mis-pedidos/${id}`;

onMounted(async () => {
  try {
    localLoading.value = true;
    try {
      const mp = await getMercadoPagoConfig();
      mercadoPagoEnabled.value = !!mp.enabled;
    } catch {
      mercadoPagoEnabled.value = false;
    }
    localStorage.removeItem('userOrders');
    localStorage.removeItem('ordersTimestamp');
    await refreshOrders();
  } catch (error) {
    console.error('Error in mounted:', error);
    toast.error('Error al cargar los pedidos');
  } finally {
    localLoading.value = false;
  }
});

const sortedOrders = computed(() => {
  if (!userOrders.value || userOrders.value.length === 0) return [];
  return [...userOrders.value]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
});

const orderLineCount = (order) => getOrderLineItems(order).length;

const previewLineItems = (order) =>
  getOrderLineItems(order).slice(0, PREVIEW_LINES);

const extraLineCount = (order) =>
  Math.max(0, getOrderLineItems(order).length - PREVIEW_LINES);

const getStatusIcon = (status) => ({
  pending: 'fas fa-clock',
  payment_pending: 'fas fa-credit-card',
  processing: 'fas fa-box',
  paid: 'fas fa-check-circle',
  completed: 'fas fa-check',
  cancelled: 'fas fa-times'
}[status] || 'fas fa-clock');

const getStatusLabel = (status) => ({
  pending: 'Pendiente',
  payment_pending: 'Pago pendiente',
  processing: 'En preparación',
  paid: 'Pagado',
  completed: 'Completado',
  cancelled: 'Cancelado'
}[status] || status);

const paymentMethodLabel = paymentMethodLabelUtil;

const formatDate = (date) => {
  return new Date(date).toLocaleString('es-AR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatPrice = (price) => {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS'
  }).format(price ?? 0);
};

const showModal = (title, message) => {
  modalTitle.value = title;
  modalMessage.value = message;
  const modal = new bootstrap.Modal(document.getElementById('confirmationModal'));
  modal.show();
};

const confirmCancelOrder = (order) => {
  selectedOrder.value = order;
  currentAction.value = 'cancelOrder';
  showModal(
    'Cancelar pedido',
    `¿Confirmás que querés cancelar el pedido #${order.id}?`
  );
};

const refreshOrders = async () => {
  try {
    localLoading.value = true;
    const orders = await getUserOrders();
    userOrders.value = orders || [];
  } catch (error) {
    console.error('Error refreshing orders:', error);
    toast.error('Error al actualizar los pedidos');
    userOrders.value = [];
  } finally {
    localLoading.value = false;
  }
};

const handleConfirmAction = async () => {
  isProcessing.value = true;
  try {
    if (currentAction.value === 'cancelOrder') {
      await cancelOrder(selectedOrder.value.id);

      toast.success('Pedido cancelado correctamente');
    }

    localStorage.removeItem('userOrders');
    localStorage.removeItem('orders');
    localStorage.removeItem('products');
    await refreshOrders();

    const modal = bootstrap.Modal.getInstance(document.getElementById('confirmationModal'));
    modal.hide();
  } catch (error) {
    console.error('Error:', error);
    toast.error('Error al procesar la acción');
  } finally {
    isProcessing.value = false;
  }
};
</script>

<style scoped>
.user-orders {
  --order-radius: 14px;
  width: 100%;
  max-width: 100%;
}

.orders-notice {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  margin: 0 0 1.25rem;
  padding: 0.75rem 1rem;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.72);
  background: rgba(255, 236, 0, 0.06);
  border: 1px solid rgba(255, 236, 0, 0.18);
  border-radius: 10px;
}

.orders-notice i {
  color: var(--primary-color, #ffec00);
  margin-top: 0.15rem;
}

.orders-stack {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.order-card {
  background: var(--panel-surface, rgba(0, 0, 0, 0.2));
  border: 1px solid var(--panel-border, rgba(255, 255, 255, 0.1));
  border-radius: var(--order-radius);
  overflow: hidden;
}

.order-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1rem 1.1rem 0.75rem;
  border-bottom: 1px solid var(--panel-border-muted, rgba(255, 255, 255, 0.06));
}

.order-card__identity {
  min-width: 0;
  text-decoration: none;
  color: inherit;
  display: block;
}

.order-card__identity:hover .order-card__id {
  color: var(--primary-color, #ffec00);
}

.order-card__id {
  display: block;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--white-color, #fff);
  line-height: 1.3;
  transition: color 0.15s;
}

.order-card__date {
  display: block;
  margin-top: 0.2rem;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.5);
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  flex-shrink: 0;
  padding: 0.35rem 0.65rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.2;
  border: 1px solid transparent;
}

.status-pill--pending,
.status-pill--payment_pending {
  background: rgba(255, 193, 7, 0.12);
  color: #ffc107;
  border-color: rgba(255, 193, 7, 0.25);
}

.status-pill--processing,
.status-pill--paid {
  background: rgba(255, 236, 0, 0.1);
  color: var(--primary-color, #ffec00);
  border-color: rgba(255, 236, 0, 0.22);
}

.status-pill--completed {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.85);
  border-color: rgba(255, 255, 255, 0.12);
}

.status-pill--cancelled {
  background: rgba(220, 53, 69, 0.12);
  color: #f08080;
  border-color: rgba(220, 53, 69, 0.25);
}

.order-card__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  list-style: none;
  margin: 0;
  padding: 0.65rem 1.1rem;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.55);
}

.order-card__meta li {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.order-card__meta i {
  color: var(--primary-color, #ffec00);
  font-size: 0.72rem;
  opacity: 0.9;
}

.order-card__meta-truncate {
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.order-card__lines {
  list-style: none;
  margin: 0;
  padding: 0.5rem 1.1rem 0.25rem;
  background: rgba(0, 0, 0, 0.08);
  border-bottom: 1px solid var(--panel-border-muted, rgba(255, 255, 255, 0.06));
}

.order-card__lines > li {
  display: grid;
  grid-template-columns: 40px 1fr auto auto;
  align-items: center;
  gap: 0.65rem;
  padding: 0.55rem 0;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.line-thumb {
  width: 40px;
  height: 40px;
  object-fit: contain;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.25);
}

.line-thumb--ph {
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.3);
  font-size: 1rem;
}

.line-body {
  min-width: 0;
}

.line-name {
  display: block;
  font-size: 0.88rem;
  font-weight: 500;
  color: var(--white-color, #fff);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.line-variant {
  display: block;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.45);
}

.line-qty {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.45);
}

.line-price {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--white-color, #fff);
  white-space: nowrap;
}

.order-card__more {
  grid-column: 1 / -1;
  padding: 0.35rem 0 0.5rem;
  font-size: 0.78rem;
  border-top: none !important;
}

.order-card__more a {
  color: var(--primary-color, #ffec00);
  text-decoration: none;
  font-weight: 500;
}

.order-card__more a:hover {
  text-decoration: underline;
}

.order-card__empty-lines {
  margin: 0;
  padding: 0.5rem 1.1rem 0;
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.45);
}

.order-card__foot {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.65rem 1rem;
  padding: 0.75rem 1.1rem;
  border-top: 1px solid var(--panel-border-muted, rgba(255, 255, 255, 0.08));
  background: rgba(0, 0, 0, 0.1);
}

.order-card__total {
  flex: 1 1 140px;
  min-width: 0;
}

.order-card__total-label {
  display: block;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(255, 255, 255, 0.45);
}

.order-card__total-value {
  display: block;
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--primary-color, #ffec00);
  line-height: 1.2;
}

.order-card__total-hint {
  display: block;
  margin-top: 0.15rem;
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.4);
}

.order-card__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.4rem;
  flex: 1 1 auto;
}

.order-card__btn {
  font-weight: 600;
  border-radius: 8px;
  white-space: nowrap;
  text-decoration: none;
}

.order-card__cancel {
  color: rgba(255, 255, 255, 0.5) !important;
  text-decoration: none;
  padding: 0.25rem 0.5rem;
  font-size: 0.8rem;
}

.order-card__cancel:hover {
  color: #f08080 !important;
}

.orders-empty {
  text-align: center;
  padding: 3rem 1.5rem;
}

.orders-empty__icon {
  width: 4rem;
  height: 4rem;
  margin: 0 auto 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(255, 236, 0, 0.08);
  color: var(--primary-color, #ffec00);
  font-size: 1.5rem;
}

.orders-empty__title {
  font-size: 1.15rem;
  font-weight: 600;
  margin-bottom: 0.35rem;
  color: var(--white-color, #fff);
}

.orders-empty__text {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.5);
  max-width: 360px;
  margin: 0 auto 1.25rem;
}

.orders-empty__btn {
  font-weight: 600;
  border-radius: 8px;
}

.orders-skeleton {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.skeleton-card {
  height: 168px;
  border-radius: var(--order-radius);
  background: linear-gradient(90deg, #2a2a2a 25%, #353535 50%, #2a2a2a 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}

:deep(.order-modal-content) {
  background: var(--grey-color, #2a2a2a) !important;
  color: var(--white-color, #fff);
  border: 1px solid rgba(255, 255, 255, 0.12);
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

@media (max-width: 576px) {
  .order-card__foot {
    flex-direction: column;
    align-items: stretch;
  }

  .order-card__toolbar {
    justify-content: flex-start;
  }

  .order-card__lines > li {
    grid-template-columns: 36px 1fr auto;
    grid-template-rows: auto auto;
  }

  .line-price {
    grid-column: 3;
    grid-row: 1 / 3;
    align-self: center;
  }
}
</style>
